import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { CustomEditFieldComponent, Form, FormMode } from '../../../../../../base/form';
import { FormGroup } from '@angular/forms';
import { LooseObject } from '../../../../../../objects/loose-object';
import { BehaviorSubject, Subscription, Subject } from 'rxjs';
import {
  data_get,
  OptionalValue,
} from '../../../../../../shared/utils/common';
import { RecordService } from '../../../../../../services/record.service';
import { FormService } from '../../../../../../services/form.service';

export interface ReportField {
  id: string;
  name: string;
  displayName: string;
  type: string;
}

@Component({
  selector: 'asset-types-report-fields',
  templateUrl: './report-fields.component.html',
  styleUrls: ['./report-fields.component.scss']
})
export class AssetTypeReportFieldsComponent implements OnInit, CustomEditFieldComponent {
  private readonly _mode$ = new BehaviorSubject<FormMode>('add');
  private readonly _field$ = new BehaviorSubject<OptionalValue<Form<any>>>(null);
  objMetadata: LooseObject = {};
  objParentForm: FormGroup;
  strModuleId: string = '';

  private deletedAttribute$ = new Subject<string>();

  setFormMode = (mode: FormMode) => this._mode$.next(mode);

  setParentForm(parentForm: FormGroup): void {
    this.objParentForm = parentForm;
  }

  setField = (field: Form<any>) => this._field$.next(field);

  constructor(
    private recordService: RecordService,
    private formService: FormService
  ) { }

  @Input() availableFields: ReportField[] = [];
  @Input() displayedFields: ReportField[] = [];

  @Input() loading = false;
  @Input() disabled = false;

  @Output() fieldsChanged = new EventEmitter<{
    available: ReportField[],
    displayed: ReportField[]
  }>();

  ngOnInit() {
    this.initializeFields();

    // Subscribe to deleted attributes
    this.formService.attributeDeleted$.subscribe(strFormKey => {
      this.availableFields = this.availableFields.filter(field => field.id !== strFormKey);
      this.displayedFields = this.displayedFields.filter(field => field.id !== strFormKey);
      this.emitFieldsChanged();
    });

    this.formService.attributeAdded$.subscribe(attr => {
      if (typeof attr === 'object' && attr.key) {
        if(attr.prevKeyValue === '') {
          //this will add new attribute on the list
          this.availableFields = [...this.availableFields, ...this.mapToReportFields([{key: attr.key, label: attr.label}])];
        } else if (attr.prevKeyValue !== attr.key) {
          // this will update an existing attribute on availableFields and displayedFields
          this.availableFields = this.availableFields.map(field =>
            field.id === attr.prevKeyValue
            ? { ...this.mapToReportFields([{key: attr.key, label: attr.label}])[0] }
            : field
          );
          this.displayedFields = this.displayedFields.map(field =>
            field.id === attr.prevKeyValue
            ? { ...this.mapToReportFields([{key: attr.key, label: attr.label}])[0] }
            : field
          );
        }
        this.emitFieldsChanged();
      }
    });
  }

  drop(event: CdkDragDrop<ReportField[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    }

    this.objParentForm.controls.asset_report_fields.setValue(this.displayedFields.map(field => field.id));

    this.emitFieldsChanged();
  }

  private initializeFields(): void {
    this.recordService.getRecordConfig('asset_types', null, true).subscribe(response => {
      const optionsField = data_get(response, 'used_fields.asset_report_fields.options') || [];
      const attributes = data_get(this.formService.getRecord('asset_types'), 'record_details.attributes') || [];
      let assetReportFields = data_get(this.formService.getRecord('asset_types'), 'record_details.asset_report_fields') || [];

      const formattedOptions = optionsField.map(option => ({
        key: option.id,
        label: option.text
      }));

      // Get keys from optionsField to avoid duplicates
      const existingKeys = new Set(formattedOptions.map(opt => opt.key));

      // Add attributes not already in optionsField
      const extraAttributes = attributes
        .filter(attr => !existingKeys.has(attr.key))
        .map(attr => ({
          key: attr.key,
          label: attr.label
        }));

      // Combine both
      const combinedOptions = [...formattedOptions, ...extraAttributes];

      const optionMap = new Map(optionsField.map(opt => [opt.id, opt.text]));
      const attributeMap = new Map(attributes.map(attr => [attr.key, attr.label]));

      // Build final list following assetReportFields order
      const selectedDisplayedFields = assetReportFields.map(field => {
        if (optionMap.has(field)) {
          return {
            key: field,
            label: optionMap.get(field)!
          };
        } else if (attributeMap.has(field)) {
          return {
            key: field,
            label: attributeMap.get(field)!
          };
        } else {
          return {
            key: field,
            label: field
          };
        }
      });

      const orderedKeys = new Set(selectedDisplayedFields.map(item => item.key));
      const availableOption = combinedOptions.filter(item => !orderedKeys.has(item.key));

      if (this._mode$.value === 'edit') {
        this.availableFields = this.mapToReportFields(availableOption);
        this.displayedFields = this.mapToReportFields(selectedDisplayedFields);
      }

      if (this._mode$.value === 'add') {
        const defaultValue = data_get(response, 'used_fields.asset_report_fields.default_value') || [];
        this.availableFields = this.mapToReportFields(availableOption);
        this.displayedFields = this.mapToReportFields(defaultValue);
        this.availableFields = this.availableFields.filter(displayed =>
          !this.displayedFields.some(field => field.id === displayed.id)
        );
      }

    });
  }

  private mapToReportFields(items: any[], typeMap?: Record<string, string>): ReportField[] {
    return items.map(item => {
      let id = item.id || item.key || item;
      if (typeof id === 'string') {
        id = id.replace(/ /g, '_');
      }

      const name = item.text || item.label || item;
      let displayName = name;
      if (displayName === 'Is Active') {
        displayName = 'Active/Inactive';
      } else if(displayName === 'variants') {
        displayName = 'Variant';
      }

      return {
        id,
        name,
        displayName,
        type: (typeMap && typeMap[item.id]) || 'text'
      };
    });
  }

  private emitFieldsChanged() {
    this.fieldsChanged.emit({
      available: [...this.availableFields],
      displayed: [...this.displayedFields]
    });
  }

  /**
   * Call this method from parent or another component to notify deletion
   */
  public notifyAttributeDeleted(strFormKey: string) {
    this.deletedAttribute$.next(strFormKey);
  }
}
